import {
  FETCH_INITIAL_DATA_FOR_MODAL,
  FETCH_INITIAL_DATA_FOR_MODAL_SUCCESS,
  FETCH_INITIAL_DATA_FOR_MODAL_FAILURE,
  IMPORTING_COMPANY_CODE,
  IMPORTING_COMPANY_CODE_SUCCESS,
  IMPORTING_COMPANY_CODE_FAILURE,
  REFRESH_OBLIGATIONS,
  REFRESH_OBLIGATIONS_SUCCESS,
  REFRESH_OBLIGATIONS_FAILURE,
} from './types';
import {
  fetchFormControlLookupData as fetchFormControlLookupDataApi,
  importCompanyCodes as importCompanyCodesApi,
  refreshObligations as refreshObligationsApi,
  fetchBusinessDescriptionsForImport as fetchBusinessDescriptionsForImportApi,
} from '../api';

import getActions from '../../shared/store/actionsFactory';
import { adminPageNames, adminPageTitles } from '../../shared/adminPageNames';
import getApi from '../../shared/apiFactory';

import Config from '../../../config';
import agGridConfig from '../../../agGridConfig';
import { normalizeCellToString } from '../../../utils/readXlsxFile.utils';

import { fetchingError, fetchProvisions } from '../../../shared/store/actions';
import { periodOrCurrentSelector } from '../../../shared/store/selectors';
import { successNotification, errorNotification } from '../../../shared/store/notificationActions';

import columnHeaders from '../xlsxImport/columnHeaders';
import { FIELD_MAPPINGS, MIN_COLUMNS_NUMBER, MAX_COLUMNS_NUMBER } from '../xlsxImport/constants';
import fixDataTypes from '../xlsxImport/fixDataTypes.processor';
import { COMPANY_SOURCES } from '../../../shared/constants';

const fetchBusinessDescriptions = async entities =>
  await fetchBusinessDescriptionsForImportApi(entities);

const fetchAfterModification = {
  dataSelector: periodOrCurrentSelector,
  actionCreator: fetchProvisions,
};

export const sharedActions = getActions(adminPageNames.COMPANY_CODES, {
  pageTitle: adminPageTitles.COMPANY_CODES,
  api: getApi({
    pageTitle: adminPageTitles.COMPANY_CODES,
    endpoints: {
      FETCH: Config.endpoints.FETCH_COMPANY_CODES_ADMIN,
      INSERT: Config.endpoints.ADD_COMPANY_CODE,
      UPDATE: Config.endpoints.UPDATE_COMPANY_CODE,
      VALIDATE: Config.endpoints.VALIDATE_COMPANY_CODES,
      IMPORT: Config.endpoints.IMPORT_COMPANY_CODES,
    },
  }),
  postProcessors: {
    extend: fetchBusinessDescriptions,
  },
  fetchAfterModification,
  sortingField: 'companyCode',
  agGridConfig: agGridConfig.adminCompanyCodes,
  excel: {
    sheetName: 'Company Codes',
    lastColumnLetter: 'AQ',
    dataTypesProcessor: fixDataTypes,
    columnHeaders,
    fieldMappings: FIELD_MAPPINGS,
    minColumnsNumber: MIN_COLUMNS_NUMBER,
    maxColumnsNumber: MAX_COLUMNS_NUMBER,
    fieldNormalizers: {
      companycode: normalizeCellToString,
      costcenter: normalizeCellToString,
    },
  },
});

export const importCompanyCodes = (companyCodes, periodName) => async dispatch => {
  dispatch({
    type: IMPORTING_COMPANY_CODE,
  });
  try {
    await importCompanyCodesApi(companyCodes, periodName);
    dispatch({
      type: IMPORTING_COMPANY_CODE_SUCCESS,
    });
  } catch (error) {
    dispatch(errorNotification(error.message));
    dispatch({
      type: IMPORTING_COMPANY_CODE_FAILURE,
    });
  }
};

export const fetchInitialDataForCompanyCodeModal = ({ period, entityToEdit }) => async dispatch => {
  dispatch({ type: FETCH_INITIAL_DATA_FOR_MODAL });

  try {
    const getFormControlLookupDataResponse = await fetchFormControlLookupDataApi({ periodName: period.name });
    let optionsFiltered = [];
    let newOptions = [];
    if (entityToEdit) {
      const obligationFiltered = getFormControlLookupDataResponse.obligations.filter(obligation =>
        obligation.connectionName === entityToEdit.connName
        && obligation.countryCode === 'US'
        && obligation.countryTaxType === 'US2'
        && obligation.enabledFlag === 'Y'
      );

      optionsFiltered = getFormControlLookupDataResponse.homeOfficeCompanyCodes.filter(item =>
        (entityToEdit.connName === item.connName
        && item.enabledFlag === 'Y'
        && entityToEdit.subledgerFlag === item.subledgerFlag)
        || (item.companyCode === entityToEdit.homeOfficeCompanyCode)
      );

      if (obligationFiltered.length > 0) {
        optionsFiltered.forEach(option => {
          obligationFiltered.forEach(obligation => {
            if (option?.companyCode === obligation?.companyCode) {
              if (!newOptions.some(existingOption => existingOption.companyCode === option.companyCode)) {
                newOptions.push(option);
              }
            }
          });
        });
      }
    } else {
      newOptions = getFormControlLookupDataResponse.homeOfficeCompanyCodes.filter(item =>
        item.enabledFlag === 'Y'
        && entityToEdit?.subledgerFlag === item?.subledgerFlag
      );
    }

    const seen = new Set();
    newOptions = newOptions.filter(item => !seen.has(item.companyCode) && seen.add(item.companyCode));

    return dispatch({
      type: FETCH_INITIAL_DATA_FOR_MODAL_SUCCESS,
      enabledFlagValues: getFormControlLookupDataResponse.enabledFlagValues,
      yesNoOptionalValues: [{ name: '', value: null }].concat(getFormControlLookupDataResponse.enabledFlagValues),
      legalEntities: getFormControlLookupDataResponse.legalEntities,
      businessUnits: getFormControlLookupDataResponse.businessUnits,
      businessCodes: getFormControlLookupDataResponse.businessCodes,
      countries: getFormControlLookupDataResponse.countries,
      chartNames: [{ chartName: null }].concat(getFormControlLookupDataResponse.chartNames),
      connectionNames: [{ connectionName: null }].concat(getFormControlLookupDataResponse.connectionNames),
      homeOfficeCompanyCodeOptions: newOptions,
      currencyCodes: [{ currencyCode: null }].concat(getFormControlLookupDataResponse.currencyCodes),
      usShareholderFlagValues: [{ name: '', value: null }].concat(getFormControlLookupDataResponse.usShareholderFlagValues),
      lineOfBusinessValues: getFormControlLookupDataResponse.lineOfBusinessValues,
      sourceOptions: [{ label: COMPANY_SOURCES.GLDRPT, value: COMPANY_SOURCES.GLDRPT },
        { label: COMPANY_SOURCES.TLS, value: COMPANY_SOURCES.TLS }],
      companyCodesOptionsToValidate: getFormControlLookupDataResponse.existingCompanyCodes,
    });
  } catch (error) {
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return dispatch({ type: FETCH_INITIAL_DATA_FOR_MODAL_FAILURE });
    }
    throw error;
  }
};

export const refreshObligations = () => async dispatch => {
  dispatch({ type: REFRESH_OBLIGATIONS });

  try {
    await refreshObligationsApi();
    dispatch(successNotification('Obligations have been refreshed.'));
    return dispatch({ type: REFRESH_OBLIGATIONS_SUCCESS });
  } catch (error) {
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return dispatch({ type: REFRESH_OBLIGATIONS_FAILURE });
    }
    throw error;
  }
};
